import m from 'mithril';

import { Button } from './Button';
import fallbackAvatar from '../assets/images/profileFallback.jpg';
import type { MithrilViewEvent } from '../types';

/**
 * Avatar picker attrs.
 */
export interface AvatarPickerAttrs extends m.Attributes {
  /** Current image value or URL. */
  value?: string | null;
  /** Display name used for alt text. */
  displayName: string;
  /** Fallback color shown behind the image. */
  color: string;
  /** Image change callback. */
  onChange: (value: string | null) => void;
}

/**
 * Renders a lightweight browser-native avatar upload workflow.
 */
export const AvatarPicker: m.ClosureComponent<AvatarPickerAttrs> = () => {
  const state = {
    input: null as HTMLInputElement | null,
  };

  const chooseFile = (): void => {
    state.input?.click();
  };

  const readFile = (file: File, attrs: AvatarPickerAttrs): void => {
    const reader = new FileReader();
    reader.onload = () => {
      attrs.onChange(typeof reader.result === 'string' ? reader.result : null);
      m.redraw();
    };
    reader.readAsDataURL(file);
  };

  return {
    view: ({ attrs }) => {
      const src = attrs.value ?? fallbackAvatar;

      return m('div', { class: 'avatar-picker' }, [
        m('button', {
          class: 'avatar-picker__preview',
          style: { backgroundColor: attrs.color },
          type: 'button',
          onclick: (event: MouseEvent & { redraw?: boolean }) => {
            event.redraw = false;
            chooseFile();
          },
        }, [
          m('img', {
            src,
            alt: `${attrs.displayName || 'MeshOS user'} profile image`,
          }),
        ]),
        m('input', {
          type: 'file',
          accept: 'image/png,image/jpeg,image/webp,image/gif',
          class: 'avatar-picker__input',
          oncreate: ({ dom }) => {
            state.input = dom as HTMLInputElement;
          },
          onchange: (event: MithrilViewEvent<HTMLInputElement>) => {
            event.redraw = false;
            const file = event.target.files?.[0];
            if (file) {
              readFile(file, attrs);
            }
          },
        }),
        m('div', { class: 'avatar-picker__actions' }, [
          m(Button, {
            appearance: 'outlined',
            className: 'avatar-picker__button avatar-picker__button--upload',
            size: 's',
            onclick: (event) => {
              event.redraw = false;
              chooseFile();
            },
          }, 'Upload Photo'),
          m(Button, {
            appearance: 'plain',
            className: 'avatar-picker__button avatar-picker__button--remove',
            variant: 'neutral',
            size: 's',
            onclick: (event) => {
              event.redraw = false;
              attrs.onChange(null);
              m.redraw();
            },
          }, 'Remove Photo'),
        ]),
      ]);
    },
  };
};
